/**
 * SEO URL Patterns Constants
 * 
 * This file defines the new SEO-friendly URL patterns for the BulletScout application.
 * These patterns replace the old ID-based URLs with human-readable SEO slugs.
 * 
 * Base URL Schemes:
 * - Listings: https://bulletscout.com/ls/{weapon_category}-ammo/{caliber}/{brand}?
 * - Products: https://bulletscout.com/pd/{weapon_category}-ammo/{caliber}/{brand}?
 * - Brands: https://bulletscout.com/brands/{brand}?
 * 
 * Where:
 * - {weapon_category} = SEO slug for weapon category (e.g., "handgun-ammo", "rifle-ammo")
 * - {caliber} = SEO slug for caliber (e.g., "9mm-luger-parabellum", "223-remington")
 * - {brand} = SEO slug for brand (e.g., "Federal", "Winchester")
 * 
 * Additional parameters like vendor_id, rounds_per_box, etc. go in query parameters.
 */

// URL Type Constants
export const URL_TYPES = {
  LISTINGS: 'ls',
  PRODUCTS: 'pd',
  BRANDS: 'brands'
};

// Route Patterns
export const ROUTE_PATTERNS = {
  // Listings view: /ls/{weapon_category}-ammo/{caliber}/{brand}?
  LISTINGS: `/${URL_TYPES.LISTINGS}/:weapon_category/:caliber?/:brand?`,
  
  // Products view: /pd/{weapon_category}-ammo/{caliber}/{brand}?
  PRODUCTS: `/${URL_TYPES.PRODUCTS}/:weapon_category/:caliber?/:brand?`,
  
  // Brands view: /brands/{brand}?
  BRANDS: `/${URL_TYPES.BRANDS}/:brand?`
};

// Facet Types from facet_meta API
export const FACET_TYPES = {
  BULLET_TYPE: 'bullet_type',
  BULLET_TYPE_S: 'bullet_type_s',
  BULLET_WEIGHT: 'bullet_weight',
  MATERIAL: 'material',
  ROUNDS_PER_BOX: 'rdsperbox',
  SHELL_LENGTH: 'shell_length',
  BRAND: 'brand',
  CALIBER: 'caliber',
  WEAPON_CATEGORY: 'weapon_category'
};

// Weapon Category SEO Slugs (from facet_meta API)
export const WEAPON_CATEGORY_SLUGS = {
  HANDGUN: 'handgun-ammo',
  RIFLE: 'rifle-ammo',
  RIMFIRE: 'rimfire-ammo',
  SHOTGUN: 'shotgun-ammo'
};

// Common Caliber SEO Slugs (examples from facet_meta API)
export const COMMON_CALIBER_SLUGS = {
  '9MM_LUGER': '9mm-luger-parabellum',
  '223_REMINGTON': '223-remington',
  '556_NATO': '556x45mm-nato',
  '308_WIN': '308-win-762x51mm',
  '22_LR': '22-long-rifle',
  '45_ACP': '45-acp-auto',
  '40_SW': '40-sw',
  '380_ACP': '380-acp-auto',
  '357_MAG': '357-magnum',
  '38_SPECIAL': '38-special'
};

// Common Brand SEO Slugs (examples from facet_meta API)
export const COMMON_BRAND_SLUGS = {
  FEDERAL: 'Federal',
  WINCHESTER: 'Winchester',
  REMINGTON: 'Remington',
  HORNADY: 'Hornady',
  BARNES: 'Barnes',
  SIERRA: 'Sierra',
  NOSLER: 'Nosler',
  CCI: 'CCI',
  BLAZER: 'Blazer',
  AMERICAN_EAGLE: 'American-Eagle',
  AGUILA: 'Aguila'
};

// URL Building Helper Functions
export const buildListingsUrl = (weaponCategory, caliber, brand, queryParams = {}) => {
  let url = `/${URL_TYPES.LISTINGS}/${weaponCategory}`;
  
  if (caliber) {
    url += `/${caliber}`;
  }
  
  if (brand) {
    url += `/${brand}`;
  }
  
  const queryString = Object.keys(queryParams)
    .filter(key => queryParams[key] !== undefined && queryParams[key] !== '')
    .map(key => `${key}=${encodeURIComponent(queryParams[key])}`)
    .join('&');
  
  if (queryString) {
    url += `?${queryString}`;
  }
  
  return url;
};

export const buildProductsUrl = (weaponCategory, caliber, brand, queryParams = {}) => {
  let url = `/${URL_TYPES.PRODUCTS}/${weaponCategory}`;
  
  if (caliber) {
    url += `/${caliber}`;
  }
  
  if (brand) {
    url += `/${brand}`;
  }
  
  const queryString = Object.keys(queryParams)
    .filter(key => queryParams[key] !== undefined && queryParams[key] !== '')
    .map(key => `${key}=${encodeURIComponent(queryParams[key])}`)
    .join('&');
  
  if (queryString) {
    url += `?${queryString}`;
  }
  
  return url;
};

export const buildBrandsUrl = (brand, queryParams = {}) => {
  let url = `/${URL_TYPES.BRANDS}/${brand}`;
  
  const queryString = Object.keys(queryParams)
    .filter(key => queryParams[key] !== undefined && queryParams[key] !== '')
    .map(key => `${key}=${encodeURIComponent(queryParams[key])}`)
    .join('&');
  
  if (queryString) {
    url += `?${queryString}`;
  }
  
  return url;
};

// URL Validation Patterns
export const URL_VALIDATION_PATTERNS = {
  // Validates weapon category slug format
  WEAPON_CATEGORY: /^(handgun|rifle|rimfire|shotgun)-ammo$/,
  
  // Validates caliber slug format (alphanumeric with hyphens)
  CALIBER: /^[a-z0-9-]+$/,
  
  // Validates brand slug format (alphanumeric with hyphens and spaces)
  BRAND: /^[a-zA-Z0-9\s-]+$/,
  
  // Validates bullet type slug format
  BULLET_TYPE: /^[a-z0-9-]+$/,
  
  // Validates material slug format
  MATERIAL: /^[a-z0-9-]+$/,
  
  // Validates general slug format
  GENERAL: /^[a-zA-Z0-9\s-]+$/
};

// SEO Slug Length Limits
export const SEO_SLUG_LIMITS = {
  MIN_LENGTH: 2,
  MAX_LENGTH: 100,
  WEAPON_CATEGORY_MAX: 20,
  CALIBER_MAX: 50,
  BRAND_MAX: 50
};

// Allowed Characters for Different Slug Types
export const SEO_SLUG_CHARS = {
  WEAPON_CATEGORY: /^[a-z-]+$/,
  CALIBER: /^[a-z0-9-]+$/,
  BRAND: /^[a-zA-Z0-9\s-]+$/,
  BULLET_TYPE: /^[a-z0-9-]+$/,
  MATERIAL: /^[a-z0-9-]+$/,
  GENERAL: /^[a-zA-Z0-9\s-]+$/
};

// Default Query Parameters
export const DEFAULT_QUERY_PARAMS = {
  page: '1',
  sort_by: 'landed_cpr'
};

// URL Migration Mapping (Old to New)
export const URL_MIGRATION_MAP = {
  // Old category URLs to new weapon category URLs
  'handgun-ammo': 'handgun-ammo',
  'rifle-ammo': 'rifle-ammo',
  'rimfire-ammo': 'rimfire-ammo',
  'shotgun-ammo': 'shotgun-ammo',
  
  // Old product URLs to new product URLs
  'product': URL_TYPES.PRODUCTS,
  
  // Old brand URLs to new brand URLs
  'aguila': URL_TYPES.BRANDS
};

// SEO URL Examples for Documentation
export const SEO_URL_EXAMPLES = {
  LISTINGS: {
    '9mm Handgun Ammo': buildListingsUrl('handgun-ammo', '9mm-luger-parabellum'),
    '9mm Federal Ammo': buildListingsUrl('handgun-ammo', '9mm-luger-parabellum', 'Federal'),
    '223 Rifle Ammo': buildListingsUrl('rifle-ammo', '223-remington'),
    '22LR Rimfire Ammo': buildListingsUrl('rimfire-ammo', '22-long-rifle')
  },
  PRODUCTS: {
    '9mm Handgun Products': buildProductsUrl('handgun-ammo', '9mm-luger-parabellum'),
    '9mm Federal Products': buildProductsUrl('handgun-ammo', '9mm-luger-parabellum', 'Federal'),
    '223 Rifle Products': buildProductsUrl('rifle-ammo', '223-remington')
  },
  BRANDS: {
    'Federal Brand': buildBrandsUrl('Federal'),
    'Winchester Brand': buildBrandsUrl('Winchester'),
    'Aguila Brand': buildBrandsUrl('Aguila')
  }
};


